import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useHistory} from 'src/rerouter/index';
import {
  getUser,
  getAllowedAgency,
  getProgramQuestions,
  fetchOptionalResume,
} from 'src/action-creators/referrer-portal/user-form';
import {selectUserDetailsError} from 'src/selectors/referrer-portal';
import classify from 'src/utils/classify';
import {isReferrerPortal} from 'src/components/referral-v2/common';
import {
  CircularLoader,
  ErrorMessage,
  toastApi,
  Card,
  CardContent,
  Avatar,
  SubTitleMedium,
  BodyMedium,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/index';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';
import UserCard from 'src/components/referrer-portal/user-form/user-card';
import UserForm from 'src/components/referrer-portal/user-form/user-form';
import css from 'src/components/referrer-portal/user-form/form-container.css';


const FormContainer = () => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showSuccessPage, setShowSuccessPage] = React.useState(false);
  const [shouldAllowOptionalResume, setShouldAllowOptionalResume] =
    React.useState(false);

  const {
    location: {
      query: {
        iframe = 'false',
        utm_source = '',
        utm_medium = '',
        agency_id = null,
        program_id = null,
      },
    },
  } = history;

  const {referralLinkId = ''} = useParams();
  const isIframe = iframe === 'true';

  const userDataLoading = useAsyncDependency(() => {
    const apis: Array<any> = [];
    if (isReferrerPortal()) {
      apis.push(dispatch(getAllowedAgency(referralLinkId)));
    }
    if (!isIframe) {
      apis.push(dispatch(getUser(referralLinkId)));
    }
    if (agency_id && program_id) {
      apis.push(dispatch(getProgramQuestions(agency_id, program_id)));
    }
    return Promise.all(apis);
  }, []);

  const apiError = useSelector(selectUserDetailsError);

  const handleSubmitForm = (formData: {[key: string]: string}) => {
    const payload = {
      ...formData,
      referral_link_id: referralLinkId,
      utm_source,
      utm_medium,
    } as const;
    dispatch(reduxApi.post('form/submit', payload))
      .then(() => setShowSuccessPage(true))
      .catch((error) => {
        toastApi.show(
          toastMessage({
            message:
              error?.response?.message ||
              t('Internal_server_error', 'Internal server error'),
            semantic: 'danger',
          }),
        );
      });
    setIsSubmitting(true);
  };

  React.useEffect(() => {
    dispatch(fetchOptionalResume()).then((shouldAllowOptionalResume) => {
      setShouldAllowOptionalResume(shouldAllowOptionalResume);
    });
  }, []);

  // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'GenericError' and 'string' have no overlap.
  if (apiError === 'InvalidLinkID') {
    return (
      <>
        {isIframe && <FormPanelHeader />}
        <div className={css.body}>
          <ErrorMessage
            heading="400"
            imageVariant="notFound"
            title={t('Invalid_Referral_Link', 'Invalid Referral Link')}
          ></ErrorMessage>
        </div>
      </>
    );
  }

  if (userDataLoading) {
    return (
      <div className={css.loader}>
        <CircularLoader />
      </div>
    );
  }

  if (showSuccessPage) {
    return (
      <>
        {isIframe && <FormPanelHeader />}
        <div className={css.successContainer}>
          <Card
            classNames={{
              wrapper: classify(css.successCard, {
                [css.successCardResponsive]: isIframe,
              }),
            }}
          >
            <CardContent className={css.successContent}>
              <Avatar
                classNames={{icon: css.successIcon}}
                iconName="party-horn"
                color="green"
              />
              <SubTitleMedium>{`${t('Success', 'Success')}!`}</SubTitleMedium>
              <BodyMedium
                color="secondary"
                className={css.successDescription}
              >{`${t(
                'Thank_you_for_submitting_your_referral_request_All_status_updates_will_be_communicated_on_the_email_provided',
                'Thank you for submitting your referral request. All status updates will be communicated on the email provided',
              )}.`}</BodyMedium>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <div className={css.body}>
      {isIframe ? <FormPanelHeader /> : <UserCard />}
      <UserForm
        isSubmitting={isSubmitting}
        handleSubmitForm={handleSubmitForm}
        isIframe={isIframe}
        shouldAllowOptionalResume={shouldAllowOptionalResume}
      />
    </div>
  );
};

export default FormContainer;

const FormPanelHeader = () => {
  const {t} = useLabelTranslation();
  const handleClosePanel = () => {
    window.parent.postMessage(
      {
        type: 'CLOSE_REFER_NOW_PANEL',
        showReferNowPanel: false,
      },
      '*',
    );
  };

  return (
    <PanelHeader onCloseButtonClick={handleClosePanel}>
      {t('Refer_Now', 'Refer Now')}
    </PanelHeader>
  );
};
